"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.toLegacyPlaces = toLegacyPlaces;
exports.toLegacyCategories = toLegacyCategories;
exports.toLegacyDefects = toLegacyDefects;
exports.toLegacyHeaders = toLegacyHeaders;
const backoffice_1 = require("./backoffice");
// WordPress exposed each retivut term's editor group as `category_type`; Firestore has no such
// field. 73 (הערה) came through as its raw id, which the editor shows as `comment`.
const RETIVUT_GROUP_BY_TERM = new Map([
    ['70', 'likui'],
    ['71', 'likui'],
    ['72', 'bdika'],
    ['73', 'comment'],
    ['81', 'likui'],
    ['118', 'tiud'],
]);
const hasWordPressId = (id) => id != null && id !== '';
const legacyId = (docId, wordPressId) => hasWordPressId(wordPressId) ? String(wordPressId) : docId;
const byHebrewName = (left, right) => left.localeCompare(right, 'he');
function toLegacyPlaces(docs, counters) {
    return docs
        .map((place) => {
        const id = legacyId(place.id, place.wpTermId);
        return {
            id,
            name: String(place.name ?? '').trim(),
            counter: counters[id] || Number(place.counter ?? 0),
            source: hasWordPressId(place.wpTermId) ? 'wordpress' : 'custom',
        };
    })
        .filter((place) => place.name.length > 0);
}
function toLegacyCategories(docs, source) {
    return docs
        .filter((category) => category.type === source)
        .map((category) => ({
        id: legacyId(category.id, category.wpTermId),
        title: String(category.name ?? ''),
        source: hasWordPressId(category.wpTermId) ? 'wordpress' : 'custom',
    }))
        .sort((left, right) => byHebrewName(left.title, right.title));
}
function toLegacyDefects(docs, context) {
    const categoriesById = new Map(context.categories.map((category) => [category.id, category]));
    const placeIdByDocId = new Map(context.places.map((place) => [place.id, legacyId(place.id, place.wpTermId)]));
    // Prod holds duplicate docs for some WordPress ids; clients key the catalog by ID, so the
    // latest edit wins instead of listing the defect twice.
    const latestById = new Map();
    for (const defect of docs) {
        if (defect.type !== context.source)
            continue;
        const id = legacyId(defect.id, defect.wpId);
        const kept = latestById.get(id);
        if (!kept || (defect.updatedAt?.toMillis() ?? 0) > (kept.updatedAt?.toMillis() ?? 0)) {
            latestById.set(id, defect);
        }
    }
    return [...latestById.values()]
        .map((defect) => {
        const category = defect.categoryId ? categoriesById.get(defect.categoryId) : undefined;
        return {
            ID: legacyId(defect.id, defect.wpId),
            post_title: String(defect.title ?? ''),
            // Report text stays Hebrew; retain the other language separately for catalog search.
            ...(defect.titleRu ? { titleRu: defect.titleRu } : {}),
            post_content: defect.content,
            num: defect.num,
            category_name: category?.name,
            ...(defect.categoryId ? { categoryId: defect.categoryId } : {}),
            category_type: context.source === 'retivut'
                ? RETIVUT_GROUP_BY_TERM.get(String(category?.wpTermId)) ?? 'likui'
                : undefined,
            locations: defect.locations?.map((placeId) => placeIdByDocId.get(placeId) ?? placeId),
            ...(defect.placeStats
                ? {
                    placeStats: {
                        ...defect.placeStats,
                        places: defect.placeStats.places.map((place) => ({
                            ...place,
                            placeId: placeIdByDocId.get(place.placeId) ?? place.placeId,
                        })),
                    },
                }
                : {}),
        };
    })
        // WordPress listed the catalog by post id; Backoffice-only defects follow by title.
        .sort((left, right) => {
        const leftId = Number(left.ID);
        const rightId = Number(right.ID);
        if (Number.isNaN(leftId) || Number.isNaN(rightId)) {
            return Number.isNaN(leftId) === Number.isNaN(rightId)
                ? byHebrewName(left.post_title, right.post_title)
                : Number.isNaN(leftId) ? 1 : -1;
        }
        return leftId - rightId;
    });
}
// Saved reports and WordPress publish store the header as a numeric WordPress id, so a
// Firestore-only template is not offered until clients write `headerTemplateId`.
function toLegacyHeaders(docs) {
    const headersByName = new Map();
    for (const template of docs) {
        if (template.archivedAt != null || !hasWordPressId(template.wpId))
            continue;
        const wpId = Number(template.wpId);
        if ((0, backoffice_1.getTemplatePlacement)({ ...template, wpId }) !== 'header')
            continue;
        const name = String(template.name ?? '').trim();
        if (!headersByName.has(name))
            headersByName.set(name, { id: wpId, title: { rendered: name } });
    }
    return [...headersByName.values()];
}
